import { Button } from "../ui/button";
import { FileText, Upload, Download, RefreshCw, Trash2 } from "lucide-react";

const fmtSize = (bytes) => {
  if (!bytes) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Multi-PDF material uploader for one exam/tema. `onUpload(file, replaceFileId)`
 * must return a promise — used to upload several files sequentially when the
 * admin selects more than one at once. */
export default function TheoryFilesUploader({
  files,
  canUpload,
  busy,
  onUpload,
  onDelete,
  onDownload,
  idPrefix,
  disabledHint,
}) {
  const handleNewFiles = async (fileList) => {
    for (const file of fileList) {
      await onUpload(file);
    }
  };

  return (
    <div className="border border-gray-200 rounded-md p-3 space-y-2 bg-gray-50/50">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-xs font-medium text-gray-700">Documentación de estudio (PDF)</span>
        <div className="flex items-center gap-1">
          <input
            id={`theory-new-${idPrefix}`}
            type="file"
            accept="application/pdf,.pdf"
            multiple
            className="hidden"
            onChange={(e) => {
              const fileList = Array.from(e.target.files || []);
              if (fileList.length) handleNewFiles(fileList);
              e.target.value = "";
            }}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={!canUpload || busy}
            onClick={() => document.getElementById(`theory-new-${idPrefix}`).click()}
            data-testid={`upload-theory-${idPrefix}`}
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Añadir PDF(s)
          </Button>
        </div>
      </div>
      {!canUpload ? (
        <p className="text-xs text-gray-500 italic">{disabledHint || "Guarda primero para gestionar documentación."}</p>
      ) : files.length === 0 ? (
        <p className="text-xs text-gray-500">Sin documentación.</p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-100 rounded-md bg-white">
          {files.map((f) => (
            <li key={f.file_id} className="flex items-center justify-between gap-3 px-3 py-2 flex-wrap">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-900 truncate">{f.original_name}</span>
                <span className="text-[10px] text-gray-500 font-mono-data flex-shrink-0">{fmtSize(f.size_bytes)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => onDownload(f.file_id, f.original_name)} title="Descargar">
                  <Download className="w-3.5 h-3.5" />
                </Button>
                <input
                  id={`theory-replace-${idPrefix}-${f.file_id}`}
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) onUpload(file, f.file_id);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => document.getElementById(`theory-replace-${idPrefix}-${f.file_id}`).click()}
                  title="Reemplazar"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => onDelete(f.file_id)}
                  title="Eliminar"
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
